import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Configuration for a URL filter parameter
 */
interface UrlFilterConfig {
  /** URL parameter name */
  param: string;
  /** Default value when not in URL */
  defaultValue: string | string[] | number | null;
  /** Type of the value for parsing */
  type: 'string' | 'array' | 'number' | 'boolean';
}

/**
 * Generic type for filter definitions
 */
export type UrlFilterDefinitions = Record<string, UrlFilterConfig>;

/**
 * Infer the value type from a filter config
 */
type FilterValue<T extends UrlFilterConfig> = 
  T['type'] extends 'array' ? string[] :
  T['type'] extends 'number' ? number | null :
  T['type'] extends 'boolean' ? boolean :
  string;

/**
 * Infer the full filters object type from definitions
 */
export type FiltersFromDefinitions<T extends UrlFilterDefinitions> = {
  [K in keyof T]: FilterValue<T[K]>;
};

/**
 * Parse a URL parameter value based on its type
 */
function parseUrlValue(
  value: string | null,
  config: UrlFilterConfig
): string | string[] | number | boolean | null {
  if (value === null || value === '') {
    return config.defaultValue;
  }

  switch (config.type) {
    case 'array':
      return value.split(',').filter(v => v.length > 0);
    case 'number':
      const num = parseInt(value, 10);
      return isNaN(num) ? (config.defaultValue as number | null) : num;
    case 'boolean':
      return value === 'true';
    case 'string':
    default:
      return value;
  }
}

/**
 * Serialize a filter value for URL
 */
function serializeUrlValue(
  value: string | string[] | number | boolean | null,
  config: UrlFilterConfig
): string | null {
  // Don't include in URL if it matches the default value
  if (value === config.defaultValue) {
    return null;
  }
  
  // Handle arrays
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return null;
    }
    // Check if array equals default array
    if (Array.isArray(config.defaultValue) && 
        value.length === config.defaultValue.length &&
        value.every((v, i) => v === (config.defaultValue as string[])[i])) {
      return null;
    }
    return value.join(',');
  }
  
  // Handle null/empty
  if (value === null || value === '') {
    return null;
  }
  
  // Handle numbers
  if (typeof value === 'number') {
    return value.toString();
  }
  
  // Handle booleans
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  
  return value;
}

/**
 * Custom hook for synchronizing filter state with URL parameters
 * 
 * @param definitions Configuration object defining each filter's URL param and type
 * @returns Object with current filter values and setter function
 * 
 * @example
 * ```tsx
 * const filterDefs = {
 *   search: { param: 'q', defaultValue: '', type: 'string' },
 *   status: { param: 'status', defaultValue: 'All', type: 'string' },
 *   types: { param: 'types', defaultValue: [], type: 'array' },
 *   page: { param: 'page', defaultValue: 1, type: 'number' },
 * } as const;
 * 
 * const { filters, setFilter, setFilters, resetFilters } = useUrlFilters(filterDefs);
 * ```
 */
export function useUrlFilters<T extends UrlFilterDefinitions>(definitions: T) {
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * Parse all filter values from current URL
   */
  const filters = useMemo(() => {
    const result: Record<string, any> = {};
    
    for (const [key, config] of Object.entries(definitions)) {
      const urlValue = searchParams.get(config.param);
      result[key] = parseUrlValue(urlValue, config);
    }
    
    return result as FiltersFromDefinitions<T>;
  }, [searchParams, definitions]);

  /**
   * Update a single filter value
   */
  const setFilter = useCallback(<K extends keyof T>(
    key: K,
    value: FilterValue<T[K]>
  ) => {
    const config = definitions[key];
    
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      const serialized = serializeUrlValue(value, config);
      
      if (serialized === null) {
        newParams.delete(config.param);
      } else {
        newParams.set(config.param, serialized);
      }
      
      return newParams;
    }, { replace: true }); // Use replace to avoid polluting browser history
  }, [definitions, setSearchParams]);

  /**
   * Update multiple filter values at once
   */
  const setFilters = useCallback((
    updates: Partial<FiltersFromDefinitions<T>>
  ) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      
      for (const [key, value] of Object.entries(updates)) {
        const config = definitions[key as keyof T];
        if (!config) continue;
        
        const serialized = serializeUrlValue(value, config);
        
        if (serialized === null) {
          newParams.delete(config.param);
        } else {
          newParams.set(config.param, serialized);
        }
      }
      
      return newParams;
    }, { replace: true });
  }, [definitions, setSearchParams]);

  /**
   * Reset all filters to their default values (clears URL params)
   */
  const resetFilters = useCallback(() => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      
      for (const config of Object.values(definitions)) {
        newParams.delete(config.param);
      }
      
      return newParams;
    }, { replace: true });
  }, [definitions, setSearchParams]);

  /**
   * Check if any filters differ from their defaults
   */
  const hasActiveFilters = useMemo(() => {
    for (const [key, config] of Object.entries(definitions)) {
      const currentValue = filters[key as keyof T];
      const defaultValue = config.defaultValue;
      
      if (Array.isArray(currentValue) && Array.isArray(defaultValue)) {
        if (currentValue.length !== defaultValue.length) return true;
        if (!currentValue.every((v, i) => v === defaultValue[i])) return true;
      } else if (currentValue !== defaultValue) {
        return true;
      }
    }
    return false;
  }, [filters, definitions]);

  return {
    filters,
    setFilter,
    setFilters,
    resetFilters,
    hasActiveFilters
  };
}

// ============================================
// Pre-built filter definitions for common pages
// ============================================

/**
 * URL filter definitions for Invoice Register
 */
export const INVOICE_FILTER_DEFINITIONS = {
  search: { param: 'q', defaultValue: '', type: 'string' as const },
  status: { param: 'status', defaultValue: 'All', type: 'string' as const },
  client: { param: 'client', defaultValue: '', type: 'string' as const },
  dateRange: { param: 'dateRange', defaultValue: 'thisMonth', type: 'string' as const },
  fromDate: { param: 'from', defaultValue: '', type: 'string' as const },
  toDate: { param: 'to', defaultValue: '', type: 'string' as const },
  types: { param: 'types', defaultValue: [] as string[], type: 'array' as const },
  projectStatus: { param: 'pStatus', defaultValue: [] as string[], type: 'array' as const },
  fromInvoice: { param: 'fromInv', defaultValue: '', type: 'string' as const },
  toInvoice: { param: 'toInv', defaultValue: '', type: 'string' as const },
  page: { param: 'page', defaultValue: 1, type: 'number' as const },
  pageSize: { param: 'size', defaultValue: 25, type: 'number' as const },
};

/**
 * URL filter definitions for Non-Conformance Register
 */
export const NCR_FILTER_DEFINITIONS = {
  search: { param: 'q', defaultValue: '', type: 'string' as const },
  status: { param: 'status', defaultValue: 'All', type: 'string' as const },
  type: { param: 'type', defaultValue: [] as string[], type: 'array' as const },
  severity: { param: 'severity', defaultValue: [] as string[], type: 'array' as const },
  page: { param: 'page', defaultValue: 1, type: 'number' as const },
  pageSize: { param: 'size', defaultValue: 25, type: 'number' as const },
};

/**
 * URL filter definitions for the COR Register
 */
export const COR_FILTER_DEFINITIONS = {
  search: { param: 'q', defaultValue: '', type: 'string' as const },
  status: { param: 'status', defaultValue: 'All', type: 'string' as const },
  type: { param: 'type', defaultValue: [] as string[], type: 'array' as const },
  rating: { param: 'rating', defaultValue: [] as string[], type: 'array' as const },
  page: { param: 'page', defaultValue: 1, type: 'number' as const },
  pageSize: { param: 'size', defaultValue: 25, type: 'number' as const },
};

/**
 * URL filter definitions for Project Register
 */
export const PROJECT_FILTER_DEFINITIONS = {
  search: { param: 'q', defaultValue: '', type: 'string' as const },
  types: { param: 'types', defaultValue: [] as string[], type: 'array' as const },
  status: { param: 'status', defaultValue: [] as string[], type: 'array' as const },
  client: { param: 'client', defaultValue: '', type: 'string' as const },
  projectLead: { param: 'lead', defaultValue: [] as string[], type: 'array' as const },
  wallType: { param: 'wall', defaultValue: [] as string[], type: 'array' as const },
  roofType: { param: 'roof', defaultValue: [] as string[], type: 'array' as const },
  floorType: { param: 'floor', defaultValue: [] as string[], type: 'array' as const },
  structureType: { param: 'structure', defaultValue: [] as string[], type: 'array' as const },
  windRegion: { param: 'wind', defaultValue: [] as string[], type: 'array' as const },
  siteClassification: { param: 'site', defaultValue: [] as string[], type: 'array' as const },
  page: { param: 'page', defaultValue: 1, type: 'number' as const },
  pageSize: { param: 'size', defaultValue: 25, type: 'number' as const },
};

/**
 * URL filter definitions for Job Cards
 */
export const JOB_CARDS_FILTER_DEFINITIONS = {
  search: { param: 'q', defaultValue: '', type: 'string' as const },
  dateRange: { param: 'dateRange', defaultValue: 'allTime', type: 'string' as const },
  fromDate: { param: 'from', defaultValue: '', type: 'string' as const },
  toDate: { param: 'to', defaultValue: '', type: 'string' as const },
  projectLead: { param: 'lead', defaultValue: [] as string[], type: 'array' as const },
  status: { param: 'status', defaultValue: [] as string[], type: 'array' as const },
  types: { param: 'types', defaultValue: [] as string[], type: 'array' as const },
  client: { param: 'client', defaultValue: '', type: 'string' as const },
  projectStatus: { param: 'pStatus', defaultValue: [] as string[], type: 'array' as const },
  page: { param: 'page', defaultValue: 1, type: 'number' as const },
  pageSize: { param: 'size', defaultValue: 25, type: 'number' as const },
};

/**
 * URL filter definitions for Workflows (Kanban)
 */
export const WORKFLOW_FILTER_DEFINITIONS = {
  search: { param: 'q', defaultValue: '', type: 'string' as const },
  client: { param: 'client', defaultValue: '', type: 'string' as const },
  teamMember: { param: 'member', defaultValue: [] as string[], type: 'array' as const },
  region: { param: 'region', defaultValue: [] as string[], type: 'array' as const },
};

/**
 * URL filter definitions for All Timesheets
 */
export const ALL_TIMESHEETS_FILTER_DEFINITIONS = {
  employee: { param: 'emp', defaultValue: '', type: 'string' as const },
  week: { param: 'week', defaultValue: '', type: 'string' as const },
  // 'grid' | 'list' | 'day'
  tab: { param: 'tab', defaultValue: 'grid', type: 'string' as const },
  // Day view date, yyyy-MM-dd; '' means today if inside the week, else Monday
  day: { param: 'day', defaultValue: '', type: 'string' as const },
};

/**
 * URL filter definitions for the employee's own Timesheets page
 */
export const TIMESHEETS_FILTER_DEFINITIONS = {
  // '' means the most recent batch
  week: { param: 'week', defaultValue: '', type: 'string' as const },
  // 'week' (the grid) | 'day' | 'list'
  view: { param: 'view', defaultValue: 'week', type: 'string' as const },
  // Day view date, yyyy-MM-dd; '' means today if inside the week, else Monday
  day: { param: 'day', defaultValue: '', type: 'string' as const },
};

/**
 * URL filter definitions for Budget page
 */
export const BUDGET_FILTER_DEFINITIONS = {
  fy: { param: 'fy', defaultValue: null, type: 'number' as const },
  projectTypes: { param: 'types', defaultValue: [] as string[], type: 'array' as const },
  projectLead: { param: 'lead', defaultValue: '', type: 'string' as const },
  workflowStatuses: { param: 'status', defaultValue: [] as string[], type: 'array' as const },
  client: { param: 'client', defaultValue: '', type: 'string' as const },
  search: { param: 'q', defaultValue: '', type: 'string' as const },
};

/**
 * URL filter definitions for Documents
 * Note: createdBy empty means every author for a manager; a non-manager is always
 * limited to their own documents (by the page and, authoritatively, the RPC)
 */
export const DOCUMENT_FILTER_DEFINITIONS = {
  search: { param: 'q', defaultValue: '', type: 'string' as const },
  status: { param: 'status', defaultValue: [] as string[], type: 'array' as const },
  type: { param: 'type', defaultValue: '', type: 'string' as const },
  project: { param: 'project', defaultValue: '', type: 'string' as const },
  client: { param: 'client', defaultValue: '', type: 'string' as const },
  createdBy: { param: 'createdBy', defaultValue: '', type: 'string' as const },
  // Date-range preset (thisMonth / lastMonth / ... / custom), matching the Fees
  // and Invoices registers. fromDate/toDate only carry a custom range.
  dateRange: { param: 'dateRange', defaultValue: 'allTime', type: 'string' as const },
  fromDate: { param: 'from', defaultValue: '', type: 'string' as const },
  toDate: { param: 'to', defaultValue: '', type: 'string' as const },
  page: { param: 'page', defaultValue: 1, type: 'number' as const },
  pageSize: { param: 'size', defaultValue: 25, type: 'number' as const },
};
